import {
    WorkflowBaseType,
    WorkflowTriggerResultsType,
    ActionMetadata,
    IWorkflow,
    IWorkflowSchemaNode,
    IWorkflowSchemaTrigger,
    WORKFLOW_BASE,
    WORKFLOW_NODE,
} from 'types/rofl';
import { RecordField } from 'types/roflWorkflowActionProperty';
import { ValidationError, findError } from 'util/errors';
import { StateCreator } from 'zustand';
import { isEqual } from 'lodash';

// the following imports are needed for types even though they aren't used directly
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { devtools } from 'zustand/middleware';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { immer } from 'zustand/middleware/immer';

type ContentDataSchemaNode = IWorkflowSchemaNode | IWorkflowSchemaTrigger;
interface BaseWorkflowNode {
    hasError: boolean;
    isIncomplete: boolean;
}

export interface WorkflowTriggerNode extends BaseWorkflowNode {
    type: typeof WORKFLOW_NODE.TRIGGER;
    data?: IWorkflowSchemaTrigger;
}

/**
 * RecordFieldsWithDependencies contains the record field definitions for a node property, alongside the
 * values of the dependencies for the record field that they describe. For example, the dependencies might contain
 * a "record_type", that determines which external system record we deal with.
 */
export interface RecordFieldsWithDependencies {
    dependencies: Record<string, any>;
    fields: RecordField[];
}

/**
 * NodeRecordFields is a map of record property names to their definitions, alongside the dependencies that were
 * used to produce that value.
 */
export type NodeRecordFields = Record<string, RecordFieldsWithDependencies>;

export interface WorkflowActionNode extends BaseWorkflowNode {
    type: typeof WORKFLOW_NODE[keyof typeof WORKFLOW_NODE];
    data?: IWorkflowSchemaNode;
    recordFields?: NodeRecordFields;
}

export type WorkflowNode = WorkflowActionNode | WorkflowTriggerNode;

interface ContentData {
    [nodeId: string]: WorkflowNode;
}

export interface NodeSlice {
    /**
     * Contact-based Workflows vs. Account-based Workflows
     */
    base: WorkflowBaseType;

    /**
     * Store the list of trigger results; can be either contacts or accounts.
     */
    triggerResults: WorkflowTriggerResultsType;

    /**
     * Core data structure used to store variables in each node.
     * It is an object of key-value pairs that indexed by node IDs.
     */
    contentData: ContentData;

    /**
     * Let's dump any extra global variables here for now :).
     */
    extraData: { [name: string]: string | number | boolean | object };

    /**
     * Topology, which is a collection of node IDs, is used to represent the spatial relationship among nodes,
     * i.e. the direction of the flow.
     *
     * In the backend response, the graph layout info is embedded in a nested structure linked using pointers.
     * To achieve quick allocation of any node content, we convert the graph layout into a simple strcture.
     * For example, because the graph is always linear in M1, for now we simply store the topology in an array of IDs.
     */
    topology: string[];

    shouldShowRequiredError: boolean;

    updateBase: (base: WorkflowBaseType) => void;
    updateTriggerResults: (results: WorkflowTriggerResultsType) => void;
    updateContentData: (id?: string, data?: any) => void;
    updateExtraData: (name: string, value: string | number | boolean | object) => void;

    /**
     * Updates the record definitions for a single record property.
     */
    updateRecordFields: (id: string, recordFields?: NodeRecordFields) => void;

    /**
     * Other methods that update the error or incomplete state of a given node.
     */
    updateNodeHasError: (id?: string, hasError?: boolean) => void;
    updateNodeIsIncomplete: (id?: string, isIncomplete?: boolean) => void;
    updateContentDataFromWorkflow: (workflow: Partial<IWorkflow> | undefined) => void;
    removeNodeData: (id?: string) => void;

    showRequiredError: () => void;
    hideRequiredError: () => void;

    appendNodeInTopology: (id: string, data?: any) => void;
    removeNodeInTopology: (id: string) => void;
}
const generateTopology = (nodes: { [id: string]: IWorkflowSchemaNode }, next?: string[] | string | null): string[] => {
    if (!next || next.length <= 0) {
        return [];
    }
    const pointer = Array.isArray(next) ? next[0] : next;
    if (!nodes[pointer]) {
        return [];
    }
    return [pointer, ...generateTopology(nodes, nodes[pointer].next)];
};

/**
 * Helper method to generate the base set of metadata for a node
 */
export const generateInitialMetadata = (): ActionMetadata => {
    return { properties: {} };
};

export const createNodeSlice: StateCreator<NodeSlice, [['zustand/immer', never], ['zustand/devtools', never]], [], NodeSlice> = set => ({
    base: WORKFLOW_BASE.CONTACTS,
    triggerResults: null,
    contentData: {},
    extraData: {},
    topology: [],
    shouldShowRequiredError: false,

    updateBase: (base: WorkflowBaseType) => set(state => {
        state.base = base;
    }),
    updateTriggerResults: (results: WorkflowTriggerResultsType) => set(state => {
        if (isEqual(state.triggerResults, results)) {
            return; // same data; do nothing
        }
        state.triggerResults = results;
    }),
    updateContentData: (id?: string, data?: ContentDataSchemaNode) => set(state => {
        if (!id || !data) {
            return; // no valid info; do nothing
        }
        if (isEqual(state.contentData[id].data, data)) {
            return; // same data; do nothing
        }
        state.contentData[id].data = data;
        state.contentData[id].isIncomplete = false;
    }),
    updateExtraData: (name: string, value: string | number | boolean | object) => set(state => {
        state.extraData[name] = value;
    }),
    updateRecordFields: (id: string, recordFields?: NodeRecordFields) => set(state => {
        const node = state.contentData[id] as WorkflowActionNode;
        node.recordFields = recordFields;
    }),
    updateNodeHasError: (id?: string, hasError: boolean = true) => set(state => {
        if (!id) {
            return;
        }
        state.contentData[id].hasError = hasError;
    }),
    updateNodeIsIncomplete: (id?: string, isIncomplete: boolean = true) => set(state => {
        if (!id) {
            return;
        }
        state.contentData[id].isIncomplete = isIncomplete;
    }),
    updateContentDataFromWorkflow: (workflow: Partial<IWorkflow> | undefined) => set(state => {
        //
        // when creating a new workflow with a blank one
        //
        const isCreate = !workflow?.eid;
        const isTemplate = isCreate && !!workflow?.template_eid;
        const initialTopology = [WORKFLOW_NODE.TRIGGER, 'NODE-1'];
        if (isCreate && !isTemplate) {
            state.contentData = initialTopology.reduce((accu, id) => ({
                ...accu,
                [id]: {
                    isIncomplete: true,
                    hasError: false,
                    loading: false,
                },
            }), {});

            state.topology = initialTopology;
            return;
        }

        // determine it is a contact-based or account-based workflow
        state.base = workflow.entity_type || WORKFLOW_BASE.CONTACTS;

        const { trigger, nodes } = workflow.schema || {};
        if (!trigger || !nodes) {
            return; // missing valid schema; something is wrong
        }

        // certain errors in action node may prevent workflows from completion without resolutions
        const triggerErrors = workflow?.errors?.schema_errors?.trigger_errors || [];
        const actionErrors = workflow?.errors?.schema_errors?.action_errors || [];
        const hasFieldMappingError = findError(actionErrors, ValidationError.FIELD_MAPPING_ISSUE);

        //
        // when editing an existing workflow, dynamically generate nodes info
        //
        state.contentData = Object.entries(nodes)
            .filter(([_, data]) => state.base !== WORKFLOW_BASE.ACCOUNTS || data.type !== 'action') // ignore sub-action nodes in account-based workflows
            .reduce((accu, [id, data]) => {
                // Only consider data to be populated if it has other information besides the node eid.
                const populatedData = {
                    ...((data?.action_config || data?.action_group_config) && data),
                    ...(data?.action_group_config ? {
                        nodesContent: {
                            filter: nodes[data?.action_group_config?.nodes?.[0] || ''] || {},
                            crm: nodes[data?.action_group_config?.nodes?.[1] || ''] || {},
                        },
                    } : {}),
                };
                return {
                    ...accu,
                    [id]: {
                        type: state.base === WORKFLOW_BASE.ACCOUNTS ? WORKFLOW_NODE.ACTION_GROUP : WORKFLOW_NODE.ACTION,
                        data: Object.keys(populatedData).length > 0 ? populatedData : undefined, // ignore empty objects
                        isIncomplete: hasFieldMappingError || isTemplate,
                        hasError: actionErrors.length > 0,
                        loading: false,
                    },
                };
            },
            {
                // every graph starts with the trigger node
                trigger: {
                    type: WORKFLOW_NODE.TRIGGER,
                    data: workflow.schema?.trigger,
                    isIncomplete: isTemplate,
                    hasError: triggerErrors.length > 0,
                    loading: false,
                },
            });

        state.topology = [WORKFLOW_NODE.TRIGGER, ...generateTopology(nodes, trigger.next)];
    }),
    removeNodeData: (id?: string) => set(state => {
        if (!id) {
            return; // no valid info; do nothing
        }

        state.contentData[id].isIncomplete = true;
        state.contentData[id].hasError = false;
        delete state.contentData[id].data;
    }),

    // methods toggles the visibility of the node error
    showRequiredError: () => set(state => {
        state.shouldShowRequiredError = true;
    }),
    hideRequiredError: () => set(state => {
        state.shouldShowRequiredError = false;
    }),

    // methods for updating the topology
    appendNodeInTopology: (id: string, data?: any) => set(state => {
        if (state.topology.includes(id)) {
            return; // no duplicate nodes; do nothing
        }
        state.topology = [...state.topology, id];
        state.contentData[id] = {
            type: WORKFLOW_NODE.ACTION, // at the moment we only allow to appen action nodes
            data,
            isIncomplete: true,
            hasError: false,
        };
    }),
    removeNodeInTopology: (id: string) => set(state => {
        if (!state.topology.includes(id)) {
            return; // already removed; do nothing
        }
        state.topology = [...state.topology].filter(nodeId => nodeId !== id);
        state.contentData[id].isIncomplete = true;
        delete state.contentData[id]?.data;
    }),
});
